import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import { cn } from '@/lib/utils'
import { useAuth } from '@/context/AuthContext'
import AbcNotation from '@/components/AbcNotation'
import PlayerControls from '@/components/PlayerControls'
import TagChip from '@/components/TagChip'
import { useWarmupPlayback, PlaybackSpec } from '@/hooks/useWarmupPlayback'
import { VOICE_SLOTS } from '@/lib/voice'
import type { StepDirection, Tag, VoiceSlot, Warmup } from '@/lib/types'

const DEFAULT_ABC = `X:1
T:
M:4/4
L:1/4
Q:1/4=90
K:C
!p!(G F E D | C4)|]
w: Hmm`

// Fixed playback defaults for the author's test-listen (range is no longer authored).
const PREVIEW_REFERENCE_MIDI = 60
const PREVIEW_RANGE_LOW_MIDI = 55
const PREVIEW_RANGE_HIGH_MIDI = 67
const PREVIEW_STEP_SEMITONES = 1

interface FormState {
  name: string
  slug: string
  description: string
  purpose: string
  abcSource: string
  voice: VoiceSlot | ''
  direction: StepDirection
  tempoQpm: number
  isActive: boolean
  tagSlugs: string[]
}

const EMPTY: FormState = {
  name: '',
  slug: '',
  description: '',
  purpose: '',
  abcSource: DEFAULT_ABC,
  voice: '',
  direction: 'UP',
  tempoQpm: 90,
  isActive: true,
  tagSlugs: [],
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export default function WarmupEditorPage() {
  const { id } = useParams<{ id?: string }>()
  const isNew = !id
  const history = useHistory()
  const { user } = useAuth()

  const [form, setForm] = useState<FormState>(EMPTY)
  const [slugTouched, setSlugTouched] = useState(false)
  const [tags, setTags] = useState<Tag[]>([])
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState(isNew)
  const [ownerId, setOwnerId] = useState<number | null>(null)
  const [loadedWarmup, setLoadedWarmup] = useState<Warmup | null>(null)

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  useEffect(() => {
    apiFetch<Tag[]>('/tags').then(setTags).catch(() => {})
  }, [])

  useEffect(() => {
    if (isNew) return
    apiFetch<Warmup[]>('/warmups')
      .then((all) => {
        const w = all.find((x) => x.id === Number(id))
        if (!w) throw new Error("We couldn't find that exercise.")
        setOwnerId(w.createdByUserId)
        setLoadedWarmup(w)
        setForm({
          name: w.name,
          slug: w.slug,
          description: w.description ?? '',
          purpose: w.purpose ?? '',
          abcSource: w.abcSource,
          voice: w.voice,
          direction: w.direction,
          tempoQpm: w.tempoQpm,
          isActive: w.isActive,
          tagSlugs: w.tags.map((t) => t.slug),
        })
        setSlugTouched(true)
        setLoaded(true)
      })
      .catch((e) => setError(e.message))
  }, [id, isNew])

  const canEdit =
    isNew || !!user?.isAdmin || (ownerId != null && ownerId === user?.id)

  // Test playback drives straight off the form, so authors hear exactly what they typed
  const spec: PlaybackSpec = useMemo(
    () => ({
      abcSource: form.abcSource,
      referenceMidi: PREVIEW_REFERENCE_MIDI,
      rangeLowMidi: PREVIEW_RANGE_LOW_MIDI,
      rangeHighMidi: PREVIEW_RANGE_HIGH_MIDI,
      stepSemitones: PREVIEW_STEP_SEMITONES,
      direction: form.direction,
      tempoQpm: form.tempoQpm,
    }),
    [form.abcSource, form.direction, form.tempoQpm]
  )
  const playback = useWarmupPlayback(spec)

  const toggleTag = (slug: string) =>
    set('tagSlugs', form.tagSlugs.includes(slug) ? form.tagSlugs.filter((s) => s !== slug) : [...form.tagSlugs, slug])

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (!form.voice) {
      setError('Pick a voice slot (Ease-in, Chest, Head or Mix) before saving.')
      return
    }
    setSaving(true)
    try {
      // The API takes the tag set as slugs under "tags"; range/phaseOrder are no longer sent.
      const payload = {
        name: form.name,
        slug: form.slug,
        description: form.description,
        purpose: form.purpose,
        abcSource: form.abcSource,
        voice: form.voice,
        direction: form.direction,
        tempoQpm: form.tempoQpm,
        isActive: form.isActive,
        tags: form.tagSlugs,
      }
      if (isNew) {
        await apiFetch('/warmups', { method: 'POST', body: JSON.stringify(payload) })
      } else {
        await apiFetch(`/warmups/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
      }
      history.push('/studio')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const retire = async () => {
    if (isNew) return
    setError('')
    try {
      await apiFetch(`/warmups/${id}`, { method: 'DELETE' })
      history.push('/studio')
    } catch (err: any) {
      setError(err.message)
    }
  }

  if (!loaded && !error) {
    return <div className="mx-auto max-w-xl px-4 py-24 text-center spec-label">Loading…</div>
  }

  // Non-owner, non-admin viewers reach this page only to look; edits happen via Clone.
  if (!canEdit) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8">
        <Link to="/warmups" className="spec-label hover:text-foreground">← Exercises</Link>
        <h1 className="mt-3 font-display text-3xl font-bold">{form.name}</h1>
        <div className="mt-6 rounded-lg border border-golden bg-secondary p-4">
          <p className="spec-label mb-1">Read-only</p>
          <p className="text-sm text-muted-foreground">
            This exercise belongs to someone else, so you can't edit it directly. Use
            <strong> Clone</strong> from the Exercises page to make your own copy and change it freely.
          </p>
        </div>
        {loadedWarmup && (
          <div className="mt-6 rounded-lg border bg-card p-4 pointer-events-none">
            <AbcNotation abc={loadedWarmup.abcSource} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <Link to="/studio" className="spec-label hover:text-foreground">← Inventory</Link>
      <h1 className="mt-3 font-display text-3xl font-bold">{isNew ? 'New exercise' : `Edit: ${form.name}`}</h1>

      {error && <p className="mt-4 text-destructive">{error}</p>}

      <form onSubmit={save} className="mt-8 grid gap-8 lg:grid-cols-2">
        {/* Left: metadata */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="spec-label">Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => {
                  set('name', e.target.value)
                  if (!slugTouched) set('slug', slugify(e.target.value))
                }}
                className="mt-1 w-full rounded-md border bg-card px-3 py-2"
              />
            </div>
            <div>
              <label className="spec-label">Slug</label>
              <input
                required
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true)
                  set('slug', slugify(e.target.value))
                }}
                className="mt-1 w-full rounded-md border bg-card px-3 py-2 font-mono text-sm"
              />
            </div>
          </div>

          {/* Voice slot — the required backbone category */}
          <div>
            <label className="spec-label">Voice</label>
            <div className="mt-1 grid grid-cols-4 gap-2">
              {VOICE_SLOTS.map((v) => (
                <button
                  key={v.slug}
                  type="button"
                  onClick={() => set('voice', v.slug)}
                  aria-pressed={form.voice === v.slug}
                  className={cn(
                    'min-h-[44px] rounded-md border px-2 text-sm font-medium transition-colors',
                    form.voice === v.slug
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-card hover:border-primary/50 hover:bg-secondary'
                  )}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Mix = coordinating chest &amp; head (the passaggio), not a separate register.
            </p>
          </div>

          <div>
            <label className="spec-label">Technique — what the singer does</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border bg-card px-3 py-2"
            />
          </div>

          <div>
            <label className="spec-label">Purpose — why it works</label>
            <textarea
              value={form.purpose}
              onChange={(e) => set('purpose', e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border bg-card px-3 py-2"
            />
          </div>

          <div>
            <p className="spec-label mb-2">Tags</p>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <TagChip key={t.id} tag={t} selected={form.tagSlugs.includes(t.slug)} onClick={() => toggleTag(t.slug)} size="sm" />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="spec-label">Direction</label>
              <select
                value={form.direction}
                onChange={(e) => set('direction', e.target.value as StepDirection)}
                className="mt-1 w-full rounded-md border bg-card px-2 py-2 text-sm"
              >
                <option value="UP">Up</option>
                <option value="DOWN">Down</option>
                <option value="UP_DOWN">Up, then down</option>
              </select>
            </div>
            <div>
              <label className="spec-label">Tempo (bpm)</label>
              <input
                type="number"
                min={30}
                max={220}
                value={form.tempoQpm}
                onChange={(e) => set('tempoQpm', Number(e.target.value))}
                className="mt-1 w-full rounded-md border bg-card px-3 py-2 font-mono text-sm"
              />
            </div>
          </div>

          <label className="flex min-h-[44px] items-center gap-2">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
              className="h-4 w-4"
            />
            <span className="text-sm">Active (appears in generated warm-ups)</span>
          </label>
        </div>

        {/* Right: the music */}
        <div className="space-y-4">
          <div>
            <label className="spec-label">ABC notation — the single source of truth</label>
            <textarea
              required
              value={form.abcSource}
              onChange={(e) => set('abcSource', e.target.value)}
              rows={10}
              spellCheck={false}
              className="mt-1 w-full rounded-md border bg-card px-3 py-2 font-mono text-sm leading-relaxed"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Include M:, L:, Q:, K: headers and a w: lyric line. Dynamics: !p! !f!, hairpins:
              !crescendo(! … !crescendo)!. No backslashes.
            </p>
          </div>

          <div className="rounded-lg border bg-card p-4">
            <p className="spec-label mb-2">Live preview</p>
            <div className={cn('coil-bar', playback.isPlaying && 'is-hot')} />
            <div className="py-3">
              <AbcNotation abc={form.abcSource} transpose={playback.displayOffset} />
            </div>
            <div className={cn('coil-bar', playback.isPlaying && 'is-hot')} />
          </div>

          <PlayerControls spec={spec} playback={playback} />
        </div>

        <div className="flex items-center gap-3 lg:col-span-2">
          <button
            type="submit"
            disabled={saving}
            className="min-h-[48px] rounded-full bg-primary px-8 font-semibold text-primary-foreground disabled:opacity-60"
          >
            {saving ? 'Saving…' : isNew ? 'Add exercise' : 'Save changes'}
          </button>
          {!isNew && form.isActive && (
            <button
              type="button"
              onClick={retire}
              className="min-h-[48px] rounded-full border border-destructive px-6 font-medium text-destructive"
            >
              Delete this exercise
            </button>
          )}
        </div>
      </form>
    </div>
  )
}
